import { UserRoleType } from '@/api';
import { useState, useCallback } from 'react';

export const CreateEditUserSidebarFormDataFieldsName = {
  FirstName: 'firstName',
  LastName: 'lastName',
  EmailAddress: 'emailAddress',
  UserRole: 'userRole',
} as const;

export type BundleTypeKeys =
  (typeof CreateEditUserSidebarFormDataFieldsName)[keyof typeof CreateEditUserSidebarFormDataFieldsName];

// Role options for creating new users (excludes GLOBAL_ADMIN)
export const userRoleTypeOptionsForCreate = [
  {
    name: 'Nurse',
    code: UserRoleType.NURSE,
  },
  {
    name: 'Manager',
    code: UserRoleType.MANAGER,
  },
  {
    name: 'Admin',
    code: UserRoleType.ADMIN,
  },
  {
    name: 'Medical Records Admin',
    code: UserRoleType.MEDICAL_RECORDS_ADMIN,
  },
  {
    name: 'Lawyer',
    code: UserRoleType.LAWYER,
  },
];

// Role options for editing users (includes GLOBAL_ADMIN)
export const userRoleTypeOptionsForEdit = [
  {
    name: 'Nurse',
    code: UserRoleType.NURSE,
  },
  {
    name: 'Manager',
    code: UserRoleType.MANAGER,
  },
  {
    name: 'Admin',
    code: UserRoleType.ADMIN,
  },
  {
    name: 'Global Admin',
    code: UserRoleType.GLOBAL_ADMIN,
  },
  {
    name: 'Medical Records Admin',
    code: UserRoleType.MEDICAL_RECORDS_ADMIN,
  },
  {
    name: 'Lawyer',
    code: UserRoleType.LAWYER,
  },
];

// Default full list
export const userRoleTypeTypeOptions = userRoleTypeOptionsForEdit;

export type UserRoleOption =
  | (typeof userRoleTypeOptionsForCreate)[number]
  | (typeof userRoleTypeOptionsForEdit)[number];

export interface CreateEditUserSidebarFormData {
  [CreateEditUserSidebarFormDataFieldsName.FirstName]: string;
  [CreateEditUserSidebarFormDataFieldsName.LastName]: string;
  [CreateEditUserSidebarFormDataFieldsName.EmailAddress]: string;
  [CreateEditUserSidebarFormDataFieldsName.UserRole]: UserRoleOption;
}

type FormDataValueTypes = CreateEditUserSidebarFormData[keyof CreateEditUserSidebarFormData];

const initialFormState: CreateEditUserSidebarFormData = {
  [CreateEditUserSidebarFormDataFieldsName.FirstName]: '',
  [CreateEditUserSidebarFormDataFieldsName.LastName]: '',
  [CreateEditUserSidebarFormDataFieldsName.EmailAddress]: '',
  [CreateEditUserSidebarFormDataFieldsName.UserRole]: userRoleTypeOptionsForCreate[0],
};

export const useCreateEditCaseForm = () => {
  const [formData, setFormData] = useState<CreateEditUserSidebarFormData>(initialFormState);

  const handleValueChange = useCallback((fieldName: keyof CreateEditUserSidebarFormData, value: FormDataValueTypes) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
  }, []);

  const isFormValid = useCallback((data: CreateEditUserSidebarFormData) => {
    return data.firstName?.length > 0 && data.lastName?.length > 0 && data.emailAddress?.length > 0;
  }, []);

  const canSubmit = isFormValid(formData);

  return {
    formData,
    setFormData,
    handleValueChange,
    resetForm,
    canSubmit,
  };
};
